import { useMemo } from "react";
import type { Edge, Node } from "@xyflow/react";
import useInitialOrgchartData from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/services";
import { OrgchartType } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/FilterBar.models";
import useOrgchartStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Utils/store";
// eslint-disable-next-line
import { getInitialDataWorker } from "../workers/getOrgchartWebWorker";

const InitialDataModificationWorker: Worker | undefined =
  getInitialDataWorker();

interface IWebWorker {
  initialNodes: Node[];
  initialEdges: Edge[];
}

interface data {
  companyId: string;
  structureId: string;
  orgchartType: OrgchartType;
}

const useModifiedData = ({
  companyId,
  structureId,
  orgchartType,
}: data): {
  isLoading: boolean;
} => {
  const { data, isFetching } = useInitialOrgchartData({
    companyId,
    structureId,
    orgchartType,
  });

  return useMemo(() => {
    if (isFetching) {
      return {
        isLoading: isFetching,
      };
    }
    if (!data) {
      return {
        isLoading: isFetching,
      };
    }
    const isInitialDataModificationWorkerDefined =
      typeof InitialDataModificationWorker;

    if (isInitialDataModificationWorkerDefined !== "undefined") {
      InitialDataModificationWorker.postMessage({
        orgchartData: data,
        orgchartType,
      });

      InitialDataModificationWorker.onmessage = (
        event: MessageEvent<IWebWorker>
      ) => {
        event.data.initialNodes.forEach((node) => {
          if (node.data.hasChildren) {
            useOrgchartStore
              .getState()
              .setChildrenCollapsedStatus(
                new Map(
                  useOrgchartStore.getState().childrenCollapsedStatus
                ).set(node.id, node.id !== "root")
              );
          }
        });
        useOrgchartStore.getState().setAllNodes(event.data.initialNodes);
        useOrgchartStore
          .getState()
          .setNodes(
            event.data.initialNodes.filter((node) => node.hidden === false)
          );
        useOrgchartStore.getState().setEdges(event.data.initialEdges);
        return null;
      };
    } else if (isInitialDataModificationWorkerDefined === "undefined") {
      console.error(
        "Initial data modification failed -> InitialDataModificationWorker is undefined"
      );
    }
    return {
      isLoading: isFetching,
    };
  }, [data, isFetching, orgchartType]);
};

export default useModifiedData;
